// Modal zum Einladen eines Kunden ins Dashboard

import { useState } from 'react';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import type { Customer } from '@/lib/types';
import { inviteCustomer } from '@/lib/api';
import { isValidEmail } from '@/lib/utils';
import toast from 'react-hot-toast';

interface InviteCustomerModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function InviteCustomerModal({ customer, isOpen, onClose }: InviteCustomerModalProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!customer) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('E-Mail-Adresse ist erforderlich');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Ungültige E-Mail-Adresse');
      return;
    }

    setIsLoading(true);

    try {
      await inviteCustomer(customer.id, email, customer.name);
      toast.success(`Einladung an ${email} gesendet!`);
      setEmail('');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Senden der Einladung');
      toast.error(err.message || 'Fehler beim Senden der Einladung');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`📧 ${customer.name} einladen`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-xl">💡</span>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">So funktioniert's:</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-700">
                <li>Geben Sie die E-Mail-Adresse des Kunden ein</li>
                <li>Der Kunde erhält eine Einladungs-E-Mail</li>
                <li>Mit dem Link kann er sein Passwort setzen</li>
                <li>Danach hat er Zugang zu seinem Dashboard</li>
              </ol>
            </div>
          </div>
        </div>

        <Input
          label="E-Mail-Adresse des Kunden"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError('');
          }}
          placeholder="kunde@example.de"
          error={error}
          disabled={isLoading}
          autoFocus
        />

        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
          <strong>Hinweis:</strong> Der Kunde kann nach der Registrierung nur seine eigenen 
          Projekte und Anfragen sehen. Als Admin behalten Sie die volle Kontrolle.
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button type="submit" loading={isLoading}>
            📧 Einladung senden
          </Button>
        </div>
      </form>
    </Modal>
  );
}
